import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { Link } from 'react-router-dom'
import { Row, Col, ListGroup, Image, Card, Button, Container, Badge } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import Message from '../../components/Message'
import Loader from '../../components/Loader'
import {
  getOrderDetails,
  payOrder,
  deliverOrder,
} from '../../actions/orderActions'
import {
  ORDER_PAY_RESET,
  ORDER_DELIVER_RESET,
} from '../../constants/orderConstants'
import styles from './OrderScreen.module.scss'

const apiUrl = process.env.REACT_APP_API_URL;

// Order items store image as an ARRAY of paths relative to the BACKEND. Rendering
// `/${item.image}` pointed at the admin domain, which has no /uploads route - its
// SPA catch-all answered 200 with index.html, so the tag received HTML and showed
// a broken-image icon.
const resolveItemImage = (image) => {
  const path = Array.isArray(image) ? image[0] : image;
  if (!path) return '';
  const clean = String(path).replace(/^\/+/, '');
  if (/^https?:\/\//i.test(clean)) return clean;
  return `${apiUrl}/${clean}`;
};

const OrderScreen = ({ match, history }) => {
  const orderId = match.params.id

  const [sdkReady, setSdkReady] = useState(false)
  const [shiprocketState, setShiprocketState] = useState({
    loading: false,
    error: null,
    couriers: [],
    usedDefaults: false,
    tracking: null,
    message: null,
  })
  const [selectedCourierId, setSelectedCourierId] = useState('')

  const dispatch = useDispatch()

  const orderDetails = useSelector((state) => state.orderDetails)
  const { order, loading, error } = orderDetails

  const orderPay = useSelector((state) => state.orderPay)
  const { loading: loadingPay, success: successPay } = orderPay

  const orderDeliver = useSelector((state) => state.orderDeliver)
  const { loading: loadingDeliver, success: successDeliver } = orderDeliver

  const userLogin = useSelector((state) => state.userLogin)
  const { userInfo } = userLogin

  if (!loading && order) {
    const addDecimals = (num) => {
      return (Math.round(num * 100) / 100).toFixed(2)
    }
    const orderItems = order.orderItems || []
    order.itemsPrice = addDecimals(
      orderItems.reduce((acc, item) => acc + item.price * item.qty, 0)
    )
  }

  useEffect(() => {
    if (!userInfo) {
      history.push('/login')
    }

    const addPayPalScript = async () => {
      const { data: clientId } = await axios.get(`${apiUrl}/api/config/paypal`)
      const script = document.createElement('script')
      script.type = 'text/javascript'
      script.src = `https://www.paypal.com/sdk/js?client-id=${clientId}`
      script.async = true
      script.onload = () => {
        setSdkReady(true)
      }
      document.body.appendChild(script)
    }

    if (!order || successPay || successDeliver || order._id !== orderId) {
      dispatch({ type: ORDER_PAY_RESET })
      dispatch({ type: ORDER_DELIVER_RESET })
      dispatch(getOrderDetails(orderId))
    } else if (!order.isPaid) {
      if (!window.paypal) {
        addPayPalScript()
      } else {
        setSdkReady(true)
      }
    }
  }, [dispatch, orderId, successPay, successDeliver, order])

  const successPaymentHandler = (paymentResult) => {
    dispatch(payOrder(orderId, paymentResult))
  }

  const deliverHandler = () => {
    dispatch(deliverOrder(order))
  }

  const resetShiprocketState = () => {
    setShiprocketState({
      loading: false,
      error: null,
      couriers: [],
      usedDefaults: false,
      tracking: null,
      message: null,
    })
    setSelectedCourierId('')
  }

  useEffect(() => {
    resetShiprocketState()
  }, [orderId])

  const getShiprocketError = (error) => {
    const data = error?.response?.data
    let message = data?.message || error?.message || 'Shiprocket request failed'
    
    // Handle Shiprocket validation errors (422 responses)
    if (data?.errors && typeof data.errors === 'object') {
      const errorDetails = Object.entries(data.errors)
        .map(([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`)
        .join('; ')
      if (errorDetails) {
        message = `${message} — ${errorDetails}`
      }
    }
    
    // Handle Shiprocket API details
    if (data?.details && typeof data.details === 'object') {
      if (data.details.errors) {
        const errorDetails = Object.entries(data.details.errors)
          .map(([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`)
          .join('; ')
        if (errorDetails) {
          message = `${data.details.message || message} — ${errorDetails}`
        }
      } else if (data.details.message) {
        message = data.details.message
      }
    }
    
    return message
  }

  const shiprocketRequestConfig = () => ({
    headers: {
      Authorization: `Bearer ${userInfo?.token}`,
    },
  })

  const handleShiprocketCreate = async () => {
    try {
      setShiprocketState((prev) => ({
        ...prev,
        loading: true,
        error: null,
        message: null,
      }))
      await axios.post(
        `${apiUrl}/api/shiprocket/orders/${orderId}/create`,
        {},
        shiprocketRequestConfig()
      )
      setShiprocketState((prev) => ({
        ...prev,
        loading: false,
        message: 'Shiprocket order created',
      }))
      dispatch(getOrderDetails(orderId))
    } catch (error) {
      setShiprocketState((prev) => ({
        ...prev,
        loading: false,
        error: getShiprocketError(error),
      }))
    }
  }

  const handleShiprocketServiceability = async () => {
    try {
      setShiprocketState((prev) => ({
        ...prev,
        loading: true,
        error: null,
        message: null,
      }))
      const { data } = await axios.get(
        `${apiUrl}/api/shiprocket/orders/${orderId}/serviceability`,
        shiprocketRequestConfig()
      )
      setShiprocketState((prev) => ({
        ...prev,
        loading: false,
        couriers: data?.serviceability?.data?.available_courier_companies || [],
        usedDefaults: Boolean(data?.usedDefaults),
        message: 'Serviceability fetched',
      }))
    } catch (error) {
      setShiprocketState((prev) => ({
        ...prev,
        loading: false,
        error: getShiprocketError(error),
      }))
    }
  }

  const handleShiprocketAssign = async () => {
    if (!selectedCourierId) {
      setShiprocketState((prev) => ({
        ...prev,
        error: 'Select a courier before assigning',
      }))
      return
    }
    try {
      setShiprocketState((prev) => ({
        ...prev,
        loading: true,
        error: null,
        message: null,
      }))
      await axios.post(
        `${apiUrl}/api/shiprocket/orders/${orderId}/assign`,
        { courier_company_id: selectedCourierId },
        shiprocketRequestConfig()
      )
      setShiprocketState((prev) => ({
        ...prev,
        loading: false,
        message: 'Courier assigned',
      }))
      dispatch(getOrderDetails(orderId))
    } catch (error) {
      setShiprocketState((prev) => ({
        ...prev,
        loading: false,
        error: getShiprocketError(error),
      }))
    }
  }

  const handleShiprocketTracking = async () => {
    try {
      setShiprocketState((prev) => ({
        ...prev,
        loading: true,
        error: null,
        message: null,
      }))
      const { data } = await axios.get(
        `${apiUrl}/api/shiprocket/orders/${orderId}/tracking`,
        shiprocketRequestConfig()
      )
      setShiprocketState((prev) => ({
        ...prev,
        loading: false,
        tracking: data?.tracking_data || null,
        message: 'Tracking updated',
      }))
      dispatch(getOrderDetails(orderId))
    } catch (error) {
      setShiprocketState((prev) => ({
        ...prev,
        loading: false,
        error: getShiprocketError(error),
      }))
    }
  }

  return loading ? (
    <Loader />
  ) : error ? (
    <Message variant='danger'>{error}</Message>
  ) : !order ? (
    <Message variant='danger'>Order not found</Message>
  ) : (
    <>
      <Container className='my-3'>
        <Row className=''>
          <Col lg={8} className='mb-3'>
            <div className='d-flex align-items-center p-0 mt-3 mb-1'>
              <h2 className={styles.OrderId + " mb-0 p-0"}>Order #{order?.orderId || order?._id}</h2>
              <p className={styles.Badge + " " + styles.Paid + " m-0 me-2"}>{order.isPaid ? 'Paid' : 'Not paid'}</p>
              <p className={styles.Badge + " " + styles.Paid + " m-0"}>{order.isDelivered ? 'Delivered' : 'Not Delivered'}</p>
            </div>
            <p className={styles.Date}>
              {new Date(order.createdAt).toLocaleString('en-GB', {
                day: 'numeric',
                month: 'short',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true,
              }).replace(',', ' at')}
            </p>
          </Col>
          <Col md={8}>
            <Card className='mb-3'>
              <h4 className='commonTitle--wul p-3'>Order Items</h4>
              {order.orderItems.length === 0 ? (
                <Message>Order is empty</Message>
              ) : (
                <ListGroup variant='flush'>
                  {order.orderItems.map((item, index) => (
                    <ListGroup.Item key={index}>
                      <Row className='align-items-center p-3'>
                        <Col md={1}>
                          <Image
                            src={resolveItemImage(item.image)}
                            alt={item.name}
                            fluid
                            rounded
                          />
                        </Col>
                        <Col>
                          <Link to={`/product/${item.product}`}>
                            {item.name}
                          </Link>
                        </Col>
                        <Col md={4}>
                          {item.qty} x ₹{item.price} = ₹{item.qty * item.price}
                        </Col>
                      </Row>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}

            </Card>

            <Card>
              <ListGroup variant='flush'>
                <ListGroup.Item>
                  <h2>Order Summary</h2>
                </ListGroup.Item>
                <ListGroup.Item>
                  <Row>
                    <Col>Items</Col>
                    <Col>₹{order.itemsPrice}</Col>
                  </Row>
                </ListGroup.Item>
                <ListGroup.Item>
                  <Row>
                    <Col>Shipping</Col>
                    <Col>₹{order.shippingPrice}</Col>
                  </Row>
                </ListGroup.Item>
                <ListGroup.Item>
                  <Row>
                    <Col>Tax</Col>
                    <Col>₹{order.taxPrice}</Col>
                  </Row>
                </ListGroup.Item>
                <ListGroup.Item>
                  <Row>
                    <Col>Total</Col>
                    <Col>₹{order.totalPrice}</Col>
                  </Row>
                </ListGroup.Item>

                {userInfo &&
                  userInfo.isAdmin &&
                  order.isPaid &&
                  !order.isDelivered && (
                    <ListGroup.Item>
                      <Button
                        type='button'
                        className='btnCommon my-2 p-3'
                        onClick={deliverHandler}
                      >
                        Mark As Delivered
                      </Button>
                    </ListGroup.Item>
                  )}
              </ListGroup>
            </Card>

            <Card className='mt-3 mb-5'>
              <ListGroup variant='flush'>
                <ListGroup.Item>
                  <h4 className='commonTitle--wul'>Payment Method</h4>
                </ListGroup.Item>
                <ListGroup.Item>
                  <p className='mb-0'><strong>Method: </strong>{order.paymentMethod}
                  </p>
                </ListGroup.Item>
              </ListGroup>
            </Card>

            <Card className='mb-5'>
              <ListGroup variant='flush'>
                <ListGroup.Item>
                  <h4 className='commonTitle--wul'>Shiprocket Shipping</h4>
                </ListGroup.Item>
                <ListGroup.Item>
                  <p className='mb-1'>
                    <strong>Status:</strong>{' '}
                    {order.shiprocketOrderId ? 'Created' : 'Not created'}
                  </p>
                  {order.shiprocketOrderId && (
                    <p className='mb-1'>
                      <strong>Shiprocket Order ID:</strong> {order.shiprocketOrderId}
                    </p>
                  )}
                  {order.shiprocketShipmentId && (
                    <p className='mb-1'>
                      <strong>Shipment ID:</strong> {order.shiprocketShipmentId}
                    </p>
                  )}
                  {order.shiprocketAwb && (
                    <p className='mb-1'>
                      <strong>AWB:</strong> {order.shiprocketAwb}
                    </p>
                  )}
                  {order.shiprocketCourierName && (
                    <p className='mb-1'>
                      <strong>Courier:</strong> {order.shiprocketCourierName}
                    </p>
                  )}
                  {order.shiprocketTrackingStatus && (
                    <p className='mb-1'>
                      <strong>Tracking Status:</strong> {order.shiprocketTrackingStatus}
                    </p>
                  )}
                  {order.shiprocketLabelUrl && (
                    <p className='mb-0'>
                      <a href={order.shiprocketLabelUrl} target='_blank' rel='noreferrer'>
                        Download Label
                      </a>
                    </p>
                  )}
                </ListGroup.Item>

                {shiprocketState.error && (
                  <ListGroup.Item>
                    <Message variant='danger'>{shiprocketState.error}</Message>
                  </ListGroup.Item>
                )}

                {shiprocketState.message && (
                  <ListGroup.Item>
                    <Message variant='success'>{shiprocketState.message}</Message>
                  </ListGroup.Item>
                )}

                {shiprocketState.usedDefaults && (
                  <ListGroup.Item>
                    <Message variant='warning'>
                      Using default weight/dimensions. Add product logistics for accurate rates.
                    </Message>
                  </ListGroup.Item>
                )}

                <ListGroup.Item>
                  <div className='d-grid gap-2'>
                    <Button
                      type='button'
                      variant='dark'
                      onClick={handleShiprocketCreate}
                      disabled={shiprocketState.loading || Boolean(order.shiprocketOrderId)}
                    >
                      {order.shiprocketOrderId ? 'Order Created' : 'Create Shiprocket Order'}
                    </Button>
                    <Button
                      type='button'
                      variant='outline-dark'
                      onClick={handleShiprocketServiceability}
                      disabled={shiprocketState.loading}
                    >
                      Fetch Couriers
                    </Button>
                  </div>
                </ListGroup.Item>

                {shiprocketState.couriers.length > 0 && (
                  <ListGroup.Item>
                    <div className='mb-2'>
                      <label className='form-label'>Select Courier</label>
                      <select
                        className='form-select'
                        value={selectedCourierId}
                        onChange={(e) => setSelectedCourierId(e.target.value)}
                      >
                        <option value=''>Choose courier</option>
                        {shiprocketState.couriers.map((courier) => (
                          <option key={courier.courier_company_id} value={courier.courier_company_id}>
                            {courier.courier_name} (₹{courier.rate || courier.freight_charge || '—'})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className='d-grid gap-2'>
                      <Button
                        type='button'
                        variant='dark'
                        onClick={handleShiprocketAssign}
                        disabled={shiprocketState.loading}
                      >
                        Assign Courier
                      </Button>
                    </div>
                  </ListGroup.Item>
                )}

                <ListGroup.Item>
                  <div className='d-grid gap-2'>
                    <Button
                      type='button'
                      variant='outline-secondary'
                      onClick={handleShiprocketTracking}
                      disabled={shiprocketState.loading || !order.shiprocketAwb}
                    >
                      Refresh Tracking
                    </Button>
                  </div>
                </ListGroup.Item>

                {shiprocketState.tracking && (
                  <ListGroup.Item>
                    <p className='mb-1'>
                      <strong>Current Status:</strong>{' '}
                      {shiprocketState.tracking?.shipment_track?.[0]?.current_status || '—'}
                    </p>
                    <p className='mb-0'>
                      <strong>Last Update:</strong>{' '}
                      {shiprocketState.tracking?.shipment_track_activities?.[0]?.date || '—'}
                    </p>
                  </ListGroup.Item>
                )}
              </ListGroup>
            </Card>
          </Col>

          <Col md={4}>
            <Card className={styles.CustomerCard}>

              <div className='mb-2'>
                <p className='mb-1'><strong>Customer</strong></p>
                <p className='mb-0'>{[order.user?.firstName, order.user?.lastName].filter(Boolean).join(' ') || '—'}</p>
              </div>

              <div className='mb-2'>
                <p className='mb-1'><strong>Contact Info</strong></p>
                <p className='mb-0'>{order.user?.phone || '—'}</p>
                <p className='mb-0'>{order.user?.email || '—'}</p>
              </div>

              <div className='mb-2'>
                <p className='mb-1'><strong>Shipping Address</strong></p>
                <p className='mb-0'>
                  {order.shippingAddress.address}, {order.shippingAddress.city}{' '}
                  {order.shippingAddress.state} {order.shippingAddress.postalCode},{' '}
                  {order.shippingAddress.country}
                </p>
              </div>

              <div className='mb-2'>
                <p className='mb-1'><strong>Billing Address</strong></p>
                <p className='mb-0'>
                  {order.shippingAddress.address}, {order.shippingAddress.city}{' '}
                  {order.shippingAddress.state} {order.shippingAddress.postalCode},{' '}
                  {order.shippingAddress.country}
                </p>
              </div>

            </Card>
          </Col>
        </Row>
      </Container>
    </>
  )
}

export default OrderScreen
